import React, { useEffect, useMemo, useState } from "react";
import { contactsApi } from "@/lib/api";
import { X } from "lucide-react";
import ContactPicker from "@/components/ContactPicker";

/**
 * MultiContactPicker — shared multi-select for any "participants / attendees / members" field.
 * Uses the same central Contacts DB & inline-create flow as ContactPicker (single-select),
 * so admin's linked contact + all normal contacts flow from one place.
 *
 * Props:
 *   values: string[] contact IDs
 *   onChange: (ids: string[]) => void
 *   entityId, projectId: optional context — passed through to inline-create
 */
export default function MultiContactPicker({ values = [], onChange, entityId, projectId, placeholder = "Add participant…" }) {
  const [chips, setChips] = useState([]);
  const [pickerKey, setPickerKey] = useState(0);

  useEffect(() => {
    const need = (values || []).filter(id => !chips.find(c => c.id === id));
    if (need.length === 0 && chips.length !== (values || []).length) {
      setChips((chips || []).filter(c => (values || []).includes(c.id)));
      return;
    }
    if (need.length === 0) return;
    Promise.all(need.map(id => contactsApi.get(id).then(r => r.data.contact).catch(() => null)))
      .then(res => setChips(prev => {
        const map = new Map((prev || []).map(c => [c.id, c]));
        res.filter(Boolean).forEach(c => map.set(c.id, c));
        return (values || []).map(id => map.get(id)).filter(Boolean);
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const add = (id, contact) => {
    if (!id || (values || []).includes(id)) { setPickerKey(k => k + 1); return; }
    onChange([...(values || []), id]);
    if (contact) setChips(prev => [...(prev || []), contact]);
    setPickerKey(k => k + 1);
  };

  const remove = (id) => {
    onChange((values || []).filter(x => x !== id));
    setChips(prev => (prev || []).filter(c => c.id !== id));
  };

  return (
    <div className="space-y-2" data-testid="multi-contact-picker">
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chips.map(c => (
            <div key={c.id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/10 border border-white/10 text-xs" data-testid={`participant-chip-${c.id}`}>
              <span>{c.full_name}</span>
              <button type="button" onClick={() => remove(c.id)} className="hover:bg-white/10 rounded-full p-0.5" aria-label={`Remove ${c.full_name}`}>
                <X className="w-3 h-3"/>
              </button>
            </div>
          ))}
        </div>
      )}
      <ContactPicker
        key={pickerKey}
        value=""
        onChange={(id, contact) => add(id, contact)}
        entityId={entityId}
        projectId={projectId}
      />
      <div className="text-[11px] text-white/40">Type a name or email — pick an existing person or create a new contact inline.</div>
    </div>
  );
}
